import TextField from '@material-ui/core/TextField';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import MenuItem from '@material-ui/core/MenuItem';
import Divider from '@material-ui/core/Divider';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(2),
  },
}));

export interface AIProviderOption {
  id: string;
  name: string;
  enabled: boolean;
}

export interface SyncFormValues {
  syncMode: 'group' | 'project';
  gitlabGroup: string;
  gitlabProject: string;
  defaultBranch: string;
  useFallbackBranch: boolean;
  fallbackBranchPattern: string;
  fallbackCaseSensitive: boolean;
  createCatalogInfo: boolean;
  overwriteCatalogInfo: boolean;
  owner: string;
  openMR: boolean;
  enrichWithAI: boolean;
  aiProvider: string;
}

export interface GroupOption {
  ref: string;
  name: string;
}

export interface SyncFormProps {
  values: SyncFormValues;
  onChange: (values: SyncFormValues) => void;
  providers: AIProviderOption[];
  groups?: GroupOption[];
  disabled?: boolean;
}

export const DEFAULT_FORM_VALUES: SyncFormValues = {
  syncMode: 'group',
  gitlabGroup: '',
  gitlabProject: '',
  defaultBranch: 'master',
  useFallbackBranch: false,
  fallbackBranchPattern: 'develop*',
  fallbackCaseSensitive: false,
  createCatalogInfo: false,
  overwriteCatalogInfo: false,
  owner: 'group:default/solucoes-digitais',
  openMR: false,
  enrichWithAI: false,
  aiProvider: 'openai',
};

export function filterEnabledProviders(
  providers: AIProviderOption[],
): AIProviderOption[] {
  return providers.filter(p => p.enabled);
}

export function SyncForm({ values, onChange, providers, groups = [], disabled }: SyncFormProps) {
  const classes = useStyles();
  const enabledProviders = filterEnabledProviders(providers);

  const update = (patch: Partial<SyncFormValues>) =>
    onChange({ ...values, ...patch });

  return (
    <div className={classes.form}>
      <TextField
        select
        label="Modo de sincronização"
        value={values.syncMode}
        onChange={e =>
          update({
            syncMode: e.target.value as 'group' | 'project',
            gitlabGroup: '',
            gitlabProject: '',
          })
        }
        variant="outlined"
        size="small"
        disabled={disabled}
        fullWidth
      >
        <MenuItem value="group">Grupo / Namespace</MenuItem>
        <MenuItem value="project">Repositório</MenuItem>
      </TextField>
      {values.syncMode === 'group' ? (
        <TextField
          label="GitLab Group/Namespace"
          value={values.gitlabGroup}
          onChange={e => update({ gitlabGroup: e.target.value })}
          placeholder="Ex: desenvolvimento-interno/tribo/groups"
          variant="outlined"
          size="small"
          disabled={disabled}
          required
          fullWidth
        />
      ) : (
        <TextField
          label="Caminho do projeto"
          value={values.gitlabProject}
          onChange={e => update({ gitlabProject: e.target.value })}
          placeholder="Ex: desenvolvimento-interno/tribo/meu-projeto"
          variant="outlined"
          size="small"
          disabled={disabled}
          required
          fullWidth
          helperText="Path completo do repositório no GitLab"
        />
      )}
      <TextField
        label="Branch padrão"
        value={values.defaultBranch}
        onChange={e => update({ defaultBranch: e.target.value })}
        variant="outlined"
        size="small"
        disabled={disabled}
        fullWidth
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={values.useFallbackBranch}
            onChange={e => update({ useFallbackBranch: e.target.checked })}
            disabled={disabled}
          />
        }
        label="Adicionar branch fallback"
      />
      {values.useFallbackBranch && (
        <>
          <TextField
            label="Pattern da branch fallback"
            value={values.fallbackBranchPattern}
            onChange={e => update({ fallbackBranchPattern: e.target.value })}
            variant="outlined"
            size="small"
            disabled={disabled}
            fullWidth
            helperText="Aceita curingas. Ex: develop*, release/*"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={values.fallbackCaseSensitive}
                onChange={e => update({ fallbackCaseSensitive: e.target.checked })}
                disabled={disabled}
              />
            }
            label="Case sensitive"
          />
          <Divider />
        </>
      )}
      <FormControlLabel
        control={
          <Checkbox
            checked={values.createCatalogInfo}
            onChange={e => {
              const checked = e.target.checked;
              update({
                createCatalogInfo: checked,
                // Reset dependent options if both checkboxes are unchecked
                openMR: (checked || values.overwriteCatalogInfo) ? values.openMR : false,
                enrichWithAI: (checked || values.overwriteCatalogInfo) ? values.enrichWithAI : false,
              });
            }}
            disabled={disabled}
          />
        }
        label="Criar catalog-info.yaml se não existir"
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={values.overwriteCatalogInfo}
            onChange={e => {
              const checked = e.target.checked;
              update({
                overwriteCatalogInfo: checked,
                // Reset dependent options if both checkboxes are unchecked
                openMR: (checked || values.createCatalogInfo) ? values.openMR : false,
                enrichWithAI: (checked || values.createCatalogInfo) ? values.enrichWithAI : false,
              });
            }}
            disabled={disabled}
          />
        }
        label="Reescrever catalog-info.yaml existente"
      />
      {(values.createCatalogInfo || values.overwriteCatalogInfo) && (
        <TextField
          select
          label="Owner"
          value={values.owner}
          onChange={e => update({ owner: e.target.value })}
          variant="outlined"
          size="small"
          disabled={disabled}
          fullWidth
        >
          {groups.map(g => (
            <MenuItem key={g.ref} value={g.ref}>
              {g.name}
            </MenuItem>
          ))}
        </TextField>
      )}
      {(values.createCatalogInfo || values.overwriteCatalogInfo) && (
        <FormControlLabel
          control={
            <Checkbox
              checked={values.openMR}
              onChange={e => update({ openMR: e.target.checked })}
              disabled={disabled}
            />
          }
          label="Abrir Merge Request"
        />
      )}
      {(values.createCatalogInfo || values.overwriteCatalogInfo) && (
        <FormControlLabel
          control={
            <Checkbox
              checked={values.enrichWithAI}
              onChange={e => {
                const checked = e.target.checked;
                update({
                  enrichWithAI: checked,
                  aiProvider: checked ? 'openai' : values.aiProvider,
                });
              }}
              disabled={disabled}
            />
          }
          label="Enriquecer com IA"
        />
      )}
      {values.enrichWithAI && (
        <TextField
          select
          label="Provedor de IA"
          value={values.aiProvider}
          onChange={e => update({ aiProvider: e.target.value })}
          variant="outlined"
          size="small"
          disabled={disabled}
          fullWidth
          data-testid="ai-provider-select"
        >
          {enabledProviders.map(p => (
            <MenuItem key={p.id} value={p.id}>
              {p.name}
            </MenuItem>
          ))}
        </TextField>
      )}
    </div>
  );
}
